import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { LogOut, FileText, Image, BookOpen, Phone, Palette, Target, User, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Account from './Account';
import AdCopyGenerator from './AdCopyGenerator';
import ImageGenerator from './ImageGenerator';
import Guides from './Guides';
import Support from './Support';
import BrandKit from './BrandKit';
import TargetingHelper from './TargetingHelper';
import MetaAdsAutopilot from './MetaAdsAutopilot';
import { axiosInstance } from '@/App';

const Dashboard = ({ onLogout }) => {
  const navigate = useNavigate();
  const username = localStorage.getItem('username');
  const [targetingUnlocked, setTargetingUnlocked] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [activeTab, setActiveTab] = useState('adcopy');

  useEffect(() => {
    // Check targeting unlock status
    const checkTargetingStatus = async () => {
      try {
        const response = await axiosInstance.get('/targeting/status');
        setTargetingUnlocked(response.data.unlocked);
      } catch (error) {
        console.error('Error checking targeting status:', error);
      }
    };
    checkTargetingStatus();
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0B0E14' }} data-testid="dashboard">
      {/* Show Account Settings if toggled */}
      {showAccountSettings ? (
        <div className="min-h-screen" style={{ backgroundColor: '#0B0E14' }}>
          {/* Header with Back Button */}
          <div style={{ backgroundColor: '#0B0E14', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex justify-between items-center">
                <Button
                  onClick={() => setShowAccountSettings(false)}
                  variant="outline"
                  className="flex items-center gap-2"
                  style={{ 
                    borderColor: 'rgba(0, 242, 255, 0.3)',
                    color: '#00F2FF',
                    backgroundColor: 'rgba(0, 242, 255, 0.1)'
                  }}
                >
                  ← Zurück zum Dashboard
                </Button>
                <Button
                  onClick={onLogout}
                  variant="outline"
                  className="flex items-center gap-2"
                  style={{ 
                    borderColor: 'rgba(255, 92, 0, 0.3)',
                    color: '#FF5C00',
                    backgroundColor: 'rgba(255, 92, 0, 0.1)'
                  }}
                >
                  <LogOut className="w-4 h-4" />
                  Abmelden
                </Button>
              </div>
            </div>
          </div>
          <Account />
        </div>
      ) : (
        <>
          {/* Header */}
          <div style={{ backgroundColor: '#0B0E14', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex justify-between items-center gap-4 flex-wrap">
                <div>
                  <h1 className="text-2xl font-bold" style={{ color: '#F5F5F7' }}>FinAds Creator</h1>
                  <p className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>Willkommen, {username}!</p>
                </div>
                
                <div className="flex items-center gap-3">
                  {/* Landing Page Funnel CTA */}
                  <a
                    href="https://app.we-love-leads.com/payment-link/682372dfde176768278265d2"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative"
                  >
                    <Button
                      className="font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 px-6"
                      style={{
                        background: 'linear-gradient(135deg, #FF5C00 0%, #FF8A00 100%)',
                        color: 'white',
                        boxShadow: '0 0 20px rgba(255, 92, 0, 0.3)'
                      }}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <span className="hidden sm:inline">Passender Landing Page Funnel</span>
                      <span className="sm:hidden">Funnel</span>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full ml-1" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', color: 'white' }}>
                        49€/mtl
                      </span>
                    </Button>
                    <span className="absolute -top-2 -right-2 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse" style={{ backgroundColor: '#00F2FF', color: '#0B0E14' }}>
                      NEU
                    </span>
                  </a>

                  <Button
                    onClick={() => setShowAccountSettings(!showAccountSettings)}
                    variant="outline"
                    className="flex items-center gap-2"
                    style={{ 
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                      color: '#F5F5F7',
                      backgroundColor: 'transparent'
                    }}
                  >
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline">Konto</span>
                  </Button>

                  <Button
                    onClick={onLogout}
                    variant="outline"
                    className="flex items-center gap-2"
                    data-testid="logout-button"
                    style={{ 
                      borderColor: 'rgba(255, 92, 0, 0.3)',
                      color: '#FF5C00',
                      backgroundColor: 'rgba(255, 92, 0, 0.1)'
                    }}
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="hidden sm:inline">Abmelden</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Custom Tab Navigation - Components stay mounted */}
            <div className="w-full">
              {/* Tab List */}
              <div 
                className="grid w-full grid-cols-7 mb-8 h-auto p-1 rounded-xl" 
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
                data-testid="dashboard-tabs"
              >
                <button 
                  onClick={() => setActiveTab('adcopy')}
                  className="flex items-center justify-center gap-2 py-3 px-2 rounded-lg text-sm font-medium transition-all"
                  style={{ 
                    backgroundColor: activeTab === 'adcopy' ? 'rgba(0, 242, 255, 0.15)' : 'transparent',
                    color: activeTab === 'adcopy' ? '#00F2FF' : 'rgba(245, 245, 247, 0.6)',
                    border: activeTab === 'adcopy' ? '1px solid rgba(0, 242, 255, 0.3)' : '1px solid transparent'
                  }}
                  data-testid="tab-adcopy"
                >
                  <FileText className="w-4 h-4" />
                  <span className="hidden sm:inline">AdCopy</span>
                </button>
                <button 
                  onClick={() => setActiveTab('images')}
                  className="flex items-center justify-center gap-2 py-3 px-2 rounded-lg text-sm font-medium transition-all"
                  style={{ 
                    backgroundColor: activeTab === 'images' ? 'rgba(0, 242, 255, 0.15)' : 'transparent',
                    color: activeTab === 'images' ? '#00F2FF' : 'rgba(245, 245, 247, 0.6)',
                    border: activeTab === 'images' ? '1px solid rgba(0, 242, 255, 0.3)' : '1px solid transparent'
                  }}
                  data-testid="tab-images"
                >
                  <Image className="w-4 h-4" />
                  <span className="hidden sm:inline">Ad-Creatives</span>
                </button>
                <button 
                  onClick={() => setActiveTab('targeting')}
                  className="flex items-center justify-center gap-2 py-3 px-2 rounded-lg text-sm font-medium transition-all relative"
                  style={{ 
                    backgroundColor: activeTab === 'targeting' ? 'rgba(167, 139, 250, 0.15)' : 'transparent',
                    color: activeTab === 'targeting' ? '#A78BFA' : 'rgba(245, 245, 247, 0.6)',
                    border: activeTab === 'targeting' ? '1px solid rgba(167, 139, 250, 0.3)' : '1px solid transparent'
                  }}
                  data-testid="tab-targeting"
                >
                  <Target className="w-4 h-4" />
                  <span className="hidden sm:inline">Meta Targeting</span>
                  {!targetingUnlocked && (
                    <span 
                      className="absolute -top-2 -right-1 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full animate-pulse whitespace-nowrap z-10"
                      style={{
                        backgroundColor: '#FF5C00',
                        boxShadow: '0 0 10px rgba(255, 92, 0, 0.6)',
                        transform: 'rotate(-12deg)'
                      }}
                    >
                      Jetzt freischalten
                    </span>
                  )}
                </button>
                <button 
                  onClick={() => setActiveTab('brand')}
                  className="flex items-center justify-center gap-2 py-3 px-2 rounded-lg text-sm font-medium transition-all"
                  style={{ 
                    backgroundColor: activeTab === 'brand' ? 'rgba(0, 242, 255, 0.15)' : 'transparent',
                    color: activeTab === 'brand' ? '#00F2FF' : 'rgba(245, 245, 247, 0.6)',
                    border: activeTab === 'brand' ? '1px solid rgba(0, 242, 255, 0.3)' : '1px solid transparent'
                  }}
                  data-testid="tab-brand"
                >
                  <Palette className="w-4 h-4" />
                  <span className="hidden sm:inline">Brand-Kit</span>
                </button>
                <button 
                  onClick={() => setActiveTab('guides')}
                  className="flex items-center justify-center gap-2 py-3 px-2 rounded-lg text-sm font-medium transition-all"
                  style={{ 
                    backgroundColor: activeTab === 'guides' ? 'rgba(0, 242, 255, 0.15)' : 'transparent',
                    color: activeTab === 'guides' ? '#00F2FF' : 'rgba(245, 245, 247, 0.6)',
                    border: activeTab === 'guides' ? '1px solid rgba(0, 242, 255, 0.3)' : '1px solid transparent'
                  }}
                  data-testid="tab-guides"
                >
                  <BookOpen className="w-4 h-4" />
                  <span className="hidden sm:inline">Leitfäden</span>
                </button>
                <button 
                  onClick={() => setActiveTab('support')}
                  className="flex items-center justify-center gap-2 py-3 px-2 rounded-lg text-sm font-medium transition-all"
                  style={{ 
                    backgroundColor: activeTab === 'support' ? 'rgba(0, 242, 255, 0.15)' : 'transparent',
                    color: activeTab === 'support' ? '#00F2FF' : 'rgba(245, 245, 247, 0.6)',
                    border: activeTab === 'support' ? '1px solid rgba(0, 242, 255, 0.3)' : '1px solid transparent'
                  }}
                  data-testid="tab-support"
                >
                  <Phone className="w-4 h-4" />
                  <span className="hidden sm:inline">Support</span>
                </button>
                <button 
                  onClick={() => setActiveTab('meta-ads')}
                  className="flex items-center justify-center gap-2 py-3 px-2 rounded-lg text-sm font-medium transition-all"
                  style={{ 
                    backgroundColor: activeTab === 'meta-ads' ? 'rgba(255, 92, 0, 0.15)' : 'transparent',
                    color: activeTab === 'meta-ads' ? '#FF5C00' : 'rgba(245, 245, 247, 0.6)',
                    border: activeTab === 'meta-ads' ? '1px solid rgba(255, 92, 0, 0.3)' : '1px solid transparent'
                  }}
                  data-testid="tab-meta-ads"
                >
                  <Zap className="w-4 h-4" />
                  <span className="hidden sm:inline">Meta Ads</span>
                </button>
              </div>

              {/* Tab Content - ALL components stay mounted, only visibility changes */}
              <div className={activeTab === 'adcopy' ? 'block' : 'hidden'}>
                <AdCopyGenerator />
              </div>

              <div className={activeTab === 'images' ? 'block' : 'hidden'}>
                <ImageGenerator />
              </div>

              <div className={activeTab === 'targeting' ? 'block' : 'hidden'}>
                <TargetingHelper />
              </div>

              <div className={activeTab === 'brand' ? 'block' : 'hidden'}>
                <BrandKit />
              </div>

              <div className={activeTab === 'guides' ? 'block' : 'hidden'}>
                <Guides />
              </div>

              <div className={activeTab === 'support' ? 'block' : 'hidden'}>
                <Support />
              </div>

              <div className={activeTab === 'meta-ads' ? 'block' : 'hidden'}>
                <MetaAdsAutopilot isActive={activeTab === 'meta-ads'} />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
